import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { confirmPasswordReset } from "firebase/auth";
import { auth } from "../contexts/firebase";
import Toast from "../components/Toast";

export default function ResetPassword() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [oobCode, setOobCode] = useState("");
    const [toast, setToast] = useState({ message: "", type: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                // User is already logged in, redirect them
                navigate("/dashboard"); // or "/generate" or any other page you want
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    const backgroundImage = isMobile
        ? "https://geneartai.com/images/mobile1.png"
        : "https://geneartai.com/images/image1.png";

    // Extract oobCode from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get("oobCode");
        if (code) {
            setOobCode(code);
        } else {
            setToast({ message: "Invalid or missing reset link.", type: "error" });
        }
    }, [location.search]);

    const handleReset = async (e) => {
        e.preventDefault();
        setToast({ message: "", type: "" });
        if (!newPassword || !confirmPassword) {
            setToast({ message: "Please fill in all fields.", type: "error" });
            return;
        }

        // password rules: ≥8 chars, upper, lower, digit, special
        const pwdRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
        if (!pwdRegex.test(newPassword)) {
            setToast({ message: "Password must be ≥8 chars and include uppercase, lowercase, number, & special char.", type: "error" });
            return;
        }

        if (newPassword.length < 6) {
            setToast({ message: "Password must be at least 6 characters.", type: "error" });
            return;
        }

        if (newPassword !== confirmPassword) {
            setToast({ message: "Passwords do not match.", type: "error" });
            return;
        }

        setLoading(true);
        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            setToast({ message: "✅ Password reset successfully! Redirecting to login...", type: "success" });
            setTimeout(() => {
                navigate("/login");
            }, 3000);
        } catch (err) {
            console.error(err);
            setToast({ message: err.message || "Failed to reset password.", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-r from-[#166CB0] to-[#1D3557] text-white px-4">
            {toast.message && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ message: "", type: "" })}
                    duration={4000}
                />
            )}
            {/* Responsive Background */}
            <div
                className="fixed inset-0 bg-cover bg-center z-0"
                style={{
                    backgroundImage: `url('${backgroundImage}')`,
                    backgroundAttachment: isMobile ? "scroll" : "fixed",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0"></div>
            </div>
            <div className="relative z-10 flex flex-col min-h-screen">
                <div className="flex-grow flex items-center justify-center px-4 py-8 pt-20">
                    <div className="bg-black/30 backdrop-blur-md p-8 rounded-xl shadow-lg w-full max-w-md">
                        <h1 className="text-xl font-bold text-center mb-6">Reset Your Password 🔒</h1>

                        <form onSubmit={handleReset} className="space-y-6">
                            <div>
                                <label className="block mb-1 font-semibold">New Password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg bg-black/20 border border-gray-600 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                                    placeholder="Enter new password"
                                />
                            </div>

                            <div>
                                <label className="block mb-1 font-semibold">Confirm New Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg bg-black/20 border border-gray-600 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                                    placeholder="Confirm new password"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full py-1.5 rounded-full font-semibold text-lg transition ${loading
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-yellow-400 text-yellow-900 hover:bg-yellow-300"
                                    }`}
                            >
                                {loading ? "Resetting..." : "Reset Password"}
                            </button>
                        </form>
                    </div>
                </div>
                {/* Footer */}
                <footer className="text-center text-xs text-gray-300 pb-3">
                    <div>© 2025 GeneArtAI — All Rights Reserved.</div>
                    <div className="mt-2">
                        <Link to="/privacy-policy" className="text-gray-300 hover:text-white underline">
                            Privacy Policy
                        </Link>{" "}
                        |{" "}
                        <Link to="/terms-of-service" className="underline hover:text-white">
                            Terms of Service
                        </Link>
                    </div>
                </footer>

                {/* Mobile Bottom Ad Spacer */}
                <div className="block sm:hidden h-20" />
            </div>
        </div>
    );
}
